import React, { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigationType } from 'react-router';
import AlertModal from '@binaa-almajal/ui-kit/components/modals/AlertModal';
import LiveToaster from './live/LiveToaster';
import Spinner from '@binaa-almajal/ui-kit/components/atoms/Spinner';
import ConnectionStatus from '@binaa-almajal/ui-kit/components/organisms/ConnectionStatus';
import BrandSplash from '@binaa-almajal/ui-kit/components/organisms/BrandSplash';
// A plain import, not lazy: the palette must open on the first keystroke, and
// a spinner behind a keyboard shortcut reads as the shortcut not working.
import CommandPalette from '@binaa-almajal/ui-kit/components/organisms/CommandPalette';
import ShortcutSheet from '@binaa-almajal/ui-kit/components/organisms/ShortcutSheet';
import { useAlert } from '@binaa-almajal/ui-kit/hooks/useAlert';
import { useI18n } from '@binaa-almajal/ui-kit/hooks/useI18n';
import { useAuth } from '@binaa-almajal/ui-kit/hooks/useAuth';
import { useConnectivity } from '@binaa-almajal/ui-kit/hooks/useConnectivity';
import { useBootSplash } from '@binaa-almajal/ui-kit/hooks/useBootSplash';
import { RequireAuth, RequireScreen } from './auth/RequireScreen';
import { ACTOR_UNMATCHED_EVENT } from './api/http';
// The two whole-tree takeovers below must NOT cover the public page: both would
// replace the client's front door with an error screen. See config/publicRoutes.js
// for what went wrong when the disconnected branch was left unscoped.
import { isPublicPath } from './config/publicRoutes';

// THE ROUTE TABLE IS DELIBERATELY ALMOST EMPTY. The copied client's routes were
// its own domain — /zakat, /budget, /treasury, /exchange-rates — and the
// screens behind them were already gone, leaving routes pointing at undefined
// identifiers. Rather than translate a finance app's navigation into Academy
// words, this starts from the routes that are certainly right.
//
// ─────────────────────────────────────────────────────────────────────────────
// `/` IS THE PUBLIC MARKETING PAGE. `/dashboard` IS THE APP HOME.
//
// `/` used to be RequireAuth → Landing (the dashboard fork). It moved, and the
// move is the thing most likely to be undone by accident, so: the Academy's
// front door is a page a stranger with no account reads. It is anonymous and
// NEVER a login redirect — the same rule as /verify and /register — and it
// renders for signed-in visitors too; only its call to action differs.
//
// Everything that assumed `/` was the app home had to move with it, and missing
// one produces a signed-in user landing on the marketing page after sign-in,
// which reads as a broken session rather than as a routing bug:
//
//   Header's brand lockup            -> /dashboard (organisms/Header.jsx)
//   ProfileMenu's post-sign-out hop  -> / (ui-kit ProfileMenu, signOutPath's
//                                    default) — sign-OUT genuinely lands on
//                                    the public page now
//   NotFound's "back home"           -> branches on auth (screens/NotFound.jsx)
//   The post-SIGN-IN target          -> auth/bffTokenSource.js, which hands the
//                                    pre-sign-in path to /api/bff/login as
//                                    returnUrl and maps `/` to /dashboard
//
// `/cockpit` (the screen's original route and nav key) redirects to
// `/dashboard` below, so bookmarks and any link still carrying the old path
// keep working.
//
// RequireAuth renders SignIn IN PLACE rather than navigating, so the URL a
// visitor asked for already survives sign-in. It is also the ONLY place a
// silent prompt=none attempt starts (PLATFORM-PLAN-9) — which is one more
// reason no public route below may ever be wrapped in it.
// ─────────────────────────────────────────────────────────────────────────────

const Landing = lazy(() => import('./screens/landing/Landing'));
// The catalogue pages the front door leads to (PLATFORM-PLAN-5 §5b.3). Lazy
// like every other route, and deliberately their OWN chunks rather than part
// of the landing one: the landing chunk is what a stranger downloads cold
// before deciding whether this company is competent, and three pages they may
// never open do not belong in it.
const AudiencePage = lazy(() => import('./screens/catalogue/AudiencePage'));
const AboutPage = lazy(() => import('./screens/marketing/AboutPage'));
const JoinPage = lazy(() => import('./screens/marketing/JoinPage'));
const CurriculumPage = lazy(() => import('./screens/catalogue/CurriculumPage'));
const ProgramPage = lazy(() => import('./screens/catalogue/ProgramPage'));
const VerifyCredential = lazy(() => import('./screens/VerifyCredential'));
const ScreeningTestPage = lazy(() => import('./screens/screening/ScreeningTestPage'));
const Checkout = lazy(() => import('./screens/subscribe/Checkout'));
const SubscriptionReturn = lazy(() => import('./screens/subscribe/Return'));
const SubscriptionRenew = lazy(() => import('./screens/subscribe/Renew'));
const NotFound = lazy(() => import('./screens/NotFound'));
const ActorUnmatched = lazy(() => import('./screens/ActorUnmatched'));

const Cockpit = lazy(() => import('./screens/cockpit/Cockpit'));
const PeopleList = lazy(() => import('./screens/people/PeopleList'));
const PersonDetail = lazy(() => import('./screens/people/PersonDetail'));
const CohortsList = lazy(() => import('./screens/cohorts/CohortsList'));
const CohortCreate = lazy(() => import('./screens/cohorts/CohortCreate'));
const CohortDetail = lazy(() => import('./screens/cohorts/CohortDetail'));
const EnrollmentDetail = lazy(() => import('./screens/enrollments/EnrollmentDetail'));
const EnrollBatch = lazy(() => import('./screens/enrollments/EnrollBatch'));
const AwaitingCompletionQueue = lazy(() => import('./screens/enrollments/AwaitingCompletionQueue'));
const CompletionBatch = lazy(() => import('./screens/enrollments/CompletionBatch'));
const GradingQueue = lazy(() => import('./screens/grading/GradingQueue'));
const AssessmentDetail = lazy(() => import('./screens/grading/AssessmentDetail'));
const QuizMarkingDetail = lazy(() => import('./screens/grading/QuizMarkingDetail'));
const ScreeningQuizMarkingDetail = lazy(() => import('./screens/grading/ScreeningQuizMarkingDetail'));
const ScreeningTaskMarkingDetail = lazy(() => import('./screens/grading/ScreeningTaskMarkingDetail'));
const Credentials = lazy(() => import('./screens/credentials/Credentials'));
const CredentialDetail = lazy(() => import('./screens/credentials/CredentialDetail'));
const VersionsList = lazy(() => import('./screens/curriculum/versions/VersionsList'));
const VersionDetail = lazy(() => import('./screens/curriculum/versions/VersionDetail'));
const ModuleDetail = lazy(() => import('./screens/curriculum/modules/ModuleDetail'));
const QuizEditor = lazy(() => import('./screens/curriculum/quizzes/QuizEditor'));
const ProgramsList = lazy(() => import('./screens/programs/ProgramsList'));
const ProgramDetail = lazy(() => import('./screens/programs/ProgramDetail'));
const CheckCodes = lazy(() => import('./screens/checkCodes/CheckCodes'));
const Assessments = lazy(() => import('./screens/assessments/Assessments'));
const AssessmentEditor = lazy(() => import('./screens/assessments/AssessmentEditor'));
const PriceBook = lazy(() => import('./screens/priceBook/PriceBook'));
const SiteContentEditor = lazy(() => import('./screens/siteContent/SiteContentEditor'));
const MyRecords = lazy(() => import('./screens/my/MyRecords'));
const MyAssessments = lazy(() => import('./screens/my/MyAssessments'));
const MyAssessmentTest = lazy(() => import('./screens/my/MyAssessments').then((m) => ({ default: m.MyAssessmentTest })));
const MyHome = lazy(() => import('./screens/my/MyHome'));
const MyCourse = lazy(() => import('./screens/my/MyCourse'));
const MySchedule = lazy(() => import('./screens/schedule/MySchedule'));
const MyTeaching = lazy(() => import('./screens/teaching/MyTeaching'));
// THE CUSTOMER'S PORTAL, which is nine screens now rather than one. The shell
// is eager about nothing: each section is its own chunk, and a company
// administrator who only ever opens the overview downloads only that.
const CompanyPortal = lazy(() => import('./screens/company/CompanyPortal'));
const CompanyOverview = lazy(() => import('./screens/company/CompanyOverview'));
const CompanyProgrammes = lazy(() => import('./screens/company/CompanyProgrammes'));
const CompanyCohorts = lazy(() => import('./screens/company/CompanyCohorts'));
const CompanyPeople = lazy(() => import('./screens/company/CompanyPeople'));
const CompanyEnrollments = lazy(() => import('./screens/company/CompanyEnrollments'));
const CompanyRequests = lazy(() => import('./screens/company/CompanyRequests'));
const CompanyLearning = lazy(() => import('./screens/company/CompanyLearning'));
const CompanyReadiness = lazy(() => import('./screens/company/CompanyReadiness'));
const CompanyAttendance = lazy(() => import('./screens/company/CompanyAttendance'));
const CompanyPerson = lazy(() => import('./screens/company/CompanyPerson'));
const CompanyCertificates = lazy(() => import('./screens/company/CompanyCertificates'));
const CompanyReport = lazy(() => import('./screens/company/CompanyReport'));
const CompanyBilling = lazy(() => import('./screens/company/CompanyBilling'));
const CompaniesList = lazy(() => import('./screens/companies/CompaniesList'));
const ArrivalsQueue = lazy(() => import('./screens/arrivals/ArrivalsQueue'));
const CohortRequestsQueue = lazy(() => import('./screens/cohortRequests/CohortRequestsQueue'));
const LearnModule = lazy(() => import('./screens/learn/LearnModule'));
const LessonPlayer = lazy(() => import('./screens/learn/LessonPlayer'));
const InstructorAvailability = lazy(() => import('./screens/instructorAvailability/InstructorAvailability'));

/** Authenticated + screen-permission-gated (academy.<screen>.<op>). REMEMBER: this hides a door, it does not
 *  lock one — the screen behind it still handles its own 403. */
const guarded = (screen, op, element) => (
    <RequireAuth>
        <RequireScreen screen={screen} op={op}>{element}</RequireScreen>
    </RequireAuth>
);

// The AUTHENTICATED home fork, at /dashboard since the public page took `/`.
// Staff land on the dashboard; a signed-in person without staff read lands on
// THEIR OWN records — the learner portal — instead of a "forbidden" wall on the
// app's front door.
//
// THREE WAYS NOW, because a B2B customer's administrator is neither. They hold
// academy.company.view and no academy.teaching.view, so the dashboard answers them 403 and
// /my shows an empty personal record — they are not a learner, they buy seats
// for people who are. The company branch is checked BEFORE the learner
// fallback and after staff, so an Academy employee who also administers an
// account (rare, but the realm permits it) still lands on their own dashboard.
const DashboardHome = () => {
    const { canScreen, loading } = useAuth();
    if (loading) return <RouteFallback />;
    if (canScreen('academy', 'teaching', 'view')) return <Cockpit />;
    if (canScreen('academy', 'company', 'view')) return <Navigate to="/company" replace />;
    return <Navigate to="/my" replace />;
};

// B9's shared handling. actor_unmatched is a whole-session condition — every
// request this identity makes answers the same way — so it is caught ONCE,
// here, from the event the HTTP layer fires, rather than re-handled (or
// mis-handled as "no permission") by every screen. The takeover clears when
// the actor changes: an administrator linking the identity, or signing out.
//
// EXCEPT FOR STAFF, and that exception is the whole reason this reads `can`.
// Staff read is the same test the server's ActorReadScope uses to decide a
// caller reads every record, so for them an unmatched actor never means "the
// Academy has not admitted you" — it means only that this identity holds no
// Person row of its OWN, which is the ordinary state of a coordinator and of
// the bootstrap administrator. Taking the whole shell over for that would cost
// them the session (the screen's only exit is sign-out) because they opened
// /my. So the announcement is ignored here and /my explains itself in place.
const useActorUnmatched = () => {
    const { actor, canScreen } = useAuth();
    const [unmatched, setUnmatched] = useState(false);

    // Read through a ref: the listener is attached once, on purpose, and must
    // still see the CURRENT answer when the actor's permissions arrive late.
    const staff = canScreen('academy', 'trainers', 'view');
    const staffRef = useRef(staff);
    useEffect(() => { staffRef.current = staff; }, [staff]);

    useEffect(() => {
        const flag = () => { if (!staffRef.current) setUnmatched(true); };
        window.addEventListener(ACTOR_UNMATCHED_EVENT, flag);
        return () => window.removeEventListener(ACTOR_UNMATCHED_EVENT, flag);
    }, []);

    // A different actor starts unflagged. Adjusted during render rather than
    // in an effect, so the stale flag is never painted for the new actor.
    const [flaggedFor, setFlaggedFor] = useState(actor?.id);
    if (flaggedFor !== actor?.id) {
        setFlaggedFor(actor?.id);
        setUnmatched(false);
    }

    return unmatched;
};

const RouteFallback = () => {
    const { t } = useI18n();
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-50 dark:bg-gray-900">
            <div className="text-center">
                <Spinner size="lg" color="primary" center />
                <p className="mt-4 text-gray-600 dark:text-gray-400">{t('common.loading')}</p>
            </div>
        </div>
    );
};

// A NEW PAGE OPENS AT ITS TOP. LocaleRouter is a plain BrowserRouter, which has
// no scroll restoration, so the browser keeps the old document offset across a
// pushState — following a card from half-way down the landing page opened the
// curriculum or programme page half-way down too. Back/forward (POP) is left to
// the browser, which restores where the visitor was; a URL with a #fragment is
// left alone so an in-page anchor still lands on its section. 'instant' beats
// any `scroll-behavior: smooth` in CSS — a new page should not visibly scroll.
function useScrollToTopOnNavigate(pathname) {
    const navigationType = useNavigationType();
    useEffect(() => {
        if (navigationType === 'POP' || window.location.hash) return;
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }, [pathname, navigationType]);
}

function AppRoutes() {
    const { isOnline, isDisconnected, retry } = useConnectivity();
    const { isOpen: alertOpen, modalMessage, type, closeModal: closeAlert } = useAlert();
    const { loading: authLoading, isAuthenticated } = useAuth();
    const actorUnmatched = useActorUnmatched();
    const { pathname } = useLocation();
    useScrollToTopOnNavigate(pathname);

    // See PUBLIC_PATHS above: neither whole-tree takeover may cover the public
    // page, because both would replace the client's front door with an error.
    const onPublicRoute = isPublicPath(pathname);

    // The splash is an OVERLAY, not a gate: the tree below keeps mounting and
    // resolving underneath it while it is up, so the min-visible floor costs
    // nothing — by the time it fades, the first screen has already rendered
    // behind it. Gating instead would turn a brand moment into a delay.
    const splash = useBootSplash(!authLoading);

    // Rendered alongside EVERY branch below, including the two early returns:
    // booting straight into "you are offline" or into the unmatched-actor
    // takeover are exactly the moments when the panel should still be there to
    // fade off, rather than snapping away and revealing a bare error.
    const splashOverlay = splash !== 'gone' ? <BrandSplash phase={splash} /> : null;

    if (actorUnmatched && !onPublicRoute) {
        return (
            <div className="flex min-h-screen flex-col bg-gray-50 dark:bg-gray-900">
                {splashOverlay}
                <Suspense fallback={<RouteFallback />}>
                    <ActorUnmatched />
                </Suspense>
            </div>
        );
    }

    if (isDisconnected && !onPublicRoute) {
        return (
            <div className="flex min-h-screen flex-col items-center justify-center bg-gray-50 dark:bg-gray-900">
                {splashOverlay}
                <ConnectionStatus isOnline={isOnline} onRetry={retry} />
            </div>
        );
    }

    return (
        <div className="flex min-h-screen flex-col bg-gray-50 dark:bg-gray-900">
            {splashOverlay}

            {/* Eagerly mounted, outside the routed layout because it belongs
                to the app, not to a screen — and it renders nothing until
                somebody asks for it, so the cost of it being everywhere is a
                keydown listener. */}
            <CommandPalette />

            {/* `?` opens the shortcut sheet, generated from the registry the
                screens declare into (plan-6 §6d.3). Mounted here for the same
                reason the palette is: it belongs to the application, renders
                nothing until asked for, and a shortcut that is only available on
                some screens is a shortcut nobody learns. */}
            <ShortcutSheet />

            <main className="flex-1">
                <Suspense fallback={<RouteFallback />}>
                    <Routes>
                        {/* Anonymous. An employer checking a certificate has no account. */}
                        <Route path="/verify" element={<VerifyCredential />} />
                        <Route path="/verify/:publicNo" element={<VerifyCredential />} />
                        {/* Anonymous. HR's screening test, opened from the link
                            in the candidate's email: an applicant has no account
                            and never gets one (SKILL-ASSESSMENT-PLAN §1). */}
                        <Route path="/screen/:token" element={<ScreeningTestPage />} />
                        {/* /register IS GONE, deliberately: the landing page's
                            Get-in-touch form is the ONE public funnel, and it
                            posts to the CRM. Self-service registration created
                            people nobody had spoken to. */}

                        {/* ANONYMOUS, and the client's front door. Not wrapped in
                            RequireAuth and never redirected to sign-in: a
                            stranger evaluating the training offering has no
                            account and never will. It renders its OWN layout —
                            not PageLayout, which mounts the authenticated app
                            chrome. */}
                        <Route path="/" element={<Landing />} />

                        {/* ANONYMOUS, for the front door's reason exactly:
                            these are the pages it leads to, and a visitor who
                            has to sign in to read what a programme teaches has
                            been shown the door. PLATFORM-PLAN-5 §5b.3.

                            THE ORDER OF DISCLOSURE IS THE ORDER OF THESE
                            ROUTES: an audience door, then a subject, then one
                            programme. Each is its own URL because each is a
                            thing somebody bookmarks and a search engine
                            indexes — which is the whole reason decision 4 made
                            the server key them on a CODE and fall back to the
                            last published edition rather than 404 mid-curate.

                            `/for/:audience` takes the audience as a parameter
                            rather than as three literal routes: the segments
                            are the enum's own values on the wire, so a fourth
                            door would be a server change and a content entry
                            rather than a fourth line here. An unknown segment
                            redirects to the catalogue — see AudiencePage. */}
                        {/* THE TWO PAGES THE FRONT PAGE SHED ITS EDITORIAL
                            HALF ONTO. Anonymous for the front page's reason
                            exactly — a stranger who has to sign in to read who
                            teaches here, or what it costs, has been shown the
                            door — and routes rather than sections because both
                            are things people send each other: a pricing anchor
                            halfway down a scrolling page cannot be pasted into
                            a procurement thread. */}
                        <Route path="/about" element={<AboutPage />} />

                        {/* ⚠ BECOME A MEMBER, AND IT IS THE ONLY ENQUIRY FORM
                            ON THE SITE. Every *Get in touch* control — the
                            header on every page, the mobile sheet, the hero,
                            the membership cards, the programme page's own
                            button — navigates here rather than scrolling to a
                            local copy. That is what makes one form reachable
                            from any place.

                            The prices are on it because those two questions
                            are one decision: a visitor who has decided to join
                            wants to know what it costs, and a visitor reading
                            what it costs is deciding whether to ask. */}
                        <Route path="/join" element={<JoinPage />} />

                        {/* /membership shipped for one deploy before the
                            pricing and the asking were put back together. A
                            redirect rather than a deletion: it was live, so it
                            may be indexed or bookmarked, and a 404 on a page
                            somebody saved is worse than a hop. */}
                        <Route path="/membership" element={<Navigate to="/join" replace />} />

                        <Route path="/for/:audience" element={<AudiencePage />} />
                        <Route path="/curriculum/:code" element={<CurriculumPage />} />
                        <Route path="/program/:code" element={<ProgramPage />} />

                        {/* ANONYMOUS, and the only public WRITE the client
                            makes into the Academy itself. The membership cards
                            link here; it opens an ORDER and creates nobody —
                            no person, no arrivals row, no membership — then
                            hands the buyer to a payment provider. See
                            screens/subscribe/Checkout.jsx. Not RequireAuth, for
                            the landing page's reason: the person buying has no
                            account, which is what they are here to arrange. */}
                        <Route path="/subscribe/:tier" element={<Checkout />} />

                        {/* WHERE THE PROVIDER SENDS THEM BACK. Anonymous for
                            the same reason, and it WRITES NOTHING: it reads the
                            order's state by reference and prints it. The page
                            cannot confirm a payment it did not witness — the
                            server learns about that from a signed webhook — so
                            this one asks. See screens/subscribe/Return.jsx.

                            IT DOES NOT MATTER THAT ":tier" ALSO MATCHES
                            "return": React Router v6 ranks by specificity, not
                            by declaration order, and a static segment always
                            outranks a dynamic one. Worth saying, because the
                            v5 habit is to reorder these by hand and somebody
                            will eventually "fix" the order of this pair. If it
                            ever DID fall through, Checkout answers an unknown
                            tier with a redirect home rather than a broken
                            screen. */}
                        <Route path="/subscribe/return" element={<SubscriptionReturn />} />

                        {/* WHERE THE RENEWAL LETTER SENDS A MEMBER. Anonymous
                            for the same reason as the two above — the letter
                            went to an address, not to a session — and it
                            WRITES NOTHING THE ACADEMY TRUSTS: it reads the
                            renewal order by reference, prints what the Academy
                            says it is for, and on a click asks the server to
                            open the provider's page. The webhook confirms; the
                            buyer comes back to /subscribe/return. See
                            screens/subscribe/Renew.jsx. */}
                        <Route path="/subscribe/renew" element={<SubscriptionRenew />} />

                        {/* The authenticated home `/` used to be. */}
                        <Route path="/dashboard" element={<RequireAuth><DashboardHome /></RequireAuth>} />
                        {/* The screen's original route, renamed to /dashboard
                            (Cockpit -> Dashboard). Kept as a redirect so any
                            existing bookmark or link still lands somewhere
                            real instead of NotFound. */}
                        <Route path="/cockpit" element={<Navigate to="/dashboard" replace />} />
                        {/* The learner portal — academy.my on the server: the
                            route is the scope and reads are the caller's own. */}
                        {/* /my IS THE LEARNER'S HOME ("what do I do now");
                            the records it used to be are one link below it,
                            and a course opens as the learner's own page rather
                            than the staff enrolment screen. /schedule is every
                            class and due date for whoever asks — learner and
                            instructor alike. */}
                        <Route path="/my" element={guarded('my', 'view', <MyHome />)} />
                        <Route path="/my/records" element={guarded('my', 'view', <MyRecords />)} />
                        {/* HR's skills tests, signed in (SKILL-ASSESSMENT-PLAN §6.2):
                            an employee the Academy already knows takes the same
                            test the emailed link opens, from here. */}
                        <Route path="/my/assessments" element={guarded('my', 'view', <MyAssessments />)} />
                        <Route path="/my/assessments/:invitationId" element={guarded('my', 'view', <MyAssessmentTest />)} />
                        <Route path="/my/courses/:enrollmentId" element={guarded('my', 'view', <MyCourse />)} />
                        <Route path="/schedule" element={guarded('my', 'view', <MySchedule />)} />
                        <Route path="/my/teaching" element={guarded('teaching', 'view', <MyTeaching />)} />
                        {/* THE CUSTOMER'S PORTAL, and the only branch of this
                            table its role opens. Gated on academy.company.view,
                            which grants no staff screen — so everything else
                            here refuses the caller these routes exist for, and
                            the portal is deliberately self-contained: no links
                            into /people, /cohorts or /enrollments, because none
                            of them would open. Every route is also the SCOPE on
                            the server; not one takes a company id.

                            NESTED, AND THE GUARD IS ON THE PARENT. A child
                            route cannot be reached without matching its parent,
                            so one RequireScreen covers all nine — and, more to
                            the point, ONE PageLayout does: the shell mounts the
                            app chrome and reads the account once, and moving
                            between sections does not remount the navigation
                            somebody is navigating with. Hiding a door is still
                            not locking one; every /company/* endpoint refuses
                            the call on its own. */}
                        <Route path="/company" element={guarded('company', 'view', <CompanyPortal />)}>
                            <Route index element={<CompanyOverview />} />
                            <Route path="programmes" element={<CompanyProgrammes />} />
                            <Route path="cohorts" element={<CompanyCohorts />} />
                            <Route path="people" element={<CompanyPeople />} />
                            <Route path="enrollments" element={<CompanyEnrollments />} />
                            <Route path="requests" element={<CompanyRequests />} />
                            <Route path="learning" element={<CompanyLearning />} />
                            <Route path="readiness" element={<CompanyReadiness />} />
                            <Route path="attendance" element={<CompanyAttendance />} />
                            <Route path="people/:personId" element={<CompanyPerson />} />
                            <Route path="certificates" element={<CompanyCertificates />} />
                            <Route path="report" element={<CompanyReport />} />
                            <Route path="billing" element={<CompanyBilling />} />
                        </Route>
                        {/* The STAFF side of the same accounts. academy.companies.view
                            to look, and the edits inside carry
                            academy.companies.edit. */}
                        <Route path="/companies" element={guarded('companies', 'view', <CompaniesList />)} />
                        {/* ONE QUEUE, TWO SCREENS. The rows live in one table
                            and the acts (issue a login, place them) are
                            identical, but the populations are not: the CRM
                            hands over EXTERNAL customers, the HRM hands over
                            INTERNAL employees, and a coordinator reasons about
                            them differently outside the app. */}
                        {/* ONE ARRIVALS ROUTE — PLATFORM-PLAN-7 §7a.6 step 3.
                            /arrivals/hrm and /arrivals/academy were the same
                            component with `origin` hardcoded; the door is a
                            filter on the screen now. */}
                        <Route path="/arrivals" element={guarded('enrollments', 'view', <ArrivalsQueue />)} />
                        {/* The third door: what the public page sold. */}
                        <Route path="/cohort-requests" element={guarded('cohorts', 'view', <CohortRequestsQueue />)} />
                        {/* RequireAuth only — staff-or-owner-or-instructor is the
                            server's narrow, matching /enrollments/:id. */}
                        <Route path="/my/enrollments/:enrollmentId/modules/:moduleId" element={guarded('my', 'view', <LearnModule />)} />
                        {/* ONE LESSON, PLAYED (WS17a). There was no lesson
                            route at all: the module screen listed every
                            lesson's material as "open in a new tab", so a
                            learner watching a recording left the Academy to do
                            it. Same guard as the module above — RequireAuth
                            only, because staff-or-owner-or-instructor is the
                            server's narrow and duplicating it here would be a
                            second answer to who may read. */}
                        <Route path="/my/enrollments/:enrollmentId/modules/:moduleId/lessons/:lessonId" element={guarded('my', 'view', <LessonPlayer />)} />
                        {/* /trainee/:enrollmentId is GONE. It rendered the same
                            three domain components over the same enrollment as
                            /enrollments/:id below, minus the writes, and nothing
                            in the client ever linked to it — a second door to a
                            room that already had one. The server's
                            cockpit/trainee endpoint is untouched. */}
                        <Route path="/people" element={guarded('trainers', 'view', <PeopleList />)} />
                        {/* THERE IS NO /people/new. It was the add-trainer
                            screen — create the person, attach the instructor
                            profile — and both halves moved to the HRM: an
                            instructor arrives through its assignment channel,
                            and the manual attach endpoint is gone from the
                            API entirely. */}
                        <Route path="/people/:id" element={guarded('trainers', 'view', <PersonDetail />)} />
                        <Route path="/cohorts" element={guarded('cohorts', 'view', <CohortsList />)} />
                        <Route path="/cohorts/new" element={guarded('cohorts', 'add', <CohortCreate />)} />
                        {/* Staff-or-instructor on the server: the assigned
                            instructor reads their own cohort through academy.teaching.view, so
                            the Content and Engagement tabs are reachable. */}
                        <Route path="/cohorts/:id" element={guarded(['cohorts', 'teaching', 'my'], 'view', <CohortDetail />)} />
                        <Route path="/cohorts/:id/enroll-batch" element={guarded('enrollments', 'add', <EnrollBatch />)} />
                        <Route path="/completions/batch" element={guarded('assessments', 'edit', <CompletionBatch />)} />
                        {/* The queue behind the cockpit's "awaiting completion"
                            tile (PLATFORM-UX-AUDIT §4.2 item 3): the people who
                            finished and were never closed out, across cohorts. */}
                        <Route path="/enrollments" element={guarded('enrollments', 'view', <AwaitingCompletionQueue />)} />
                        {/* /registrations WAS THE ADMISSION QUEUE and is gone
                            (PLATFORM-PLAN WS7): nobody applies to the Academy
                            any more, so nothing arrives to approve. /register
                            below is a different route and still exists — it is
                            the public LEAD form. */}
                        <Route path="/grading" element={guarded('grading', 'view', <GradingQueue />)} />
                        {/* Declared BEFORE /grading/:id, which would otherwise capture it. */}
                        <Route path="/grading/quiz-attempts/:attemptId" element={guarded('grading', 'view', <QuizMarkingDetail />)} />
                        {/* HR's screening tests, marked blind (SKILL-ASSESSMENT-PLAN §5.5). */}
                        <Route path="/grading/screening/quizzes/:attemptId" element={guarded('grading', 'view', <ScreeningQuizMarkingDetail />)} />
                        <Route path="/grading/screening/tasks/:submissionId" element={guarded('grading', 'view', <ScreeningTaskMarkingDetail />)} />
                        <Route path="/grading/:id" element={guarded('grading', 'view', <AssessmentDetail />)} />
                        {/* Gated on the assessments view, not the add: looking up what
                            somebody holds is a read, and gating the whole screen
                            on issuing would hide the lookup from every
                            coordinator. The issue buttons inside carry
                            academy.assessments.add themselves. */}
                        <Route path="/credentials" element={guarded('assessments', 'view', <Credentials />)} />
                        {/* Staff-or-owner on the server, like enrollments. */}
                        <Route path="/credentials/:id" element={guarded(['assessments', 'my'], 'view', <CredentialDetail />)} />
                        <Route path="/curriculum" element={guarded('courses', 'view', <VersionsList />)} />
                        <Route path="/curriculum/versions/:id" element={guarded('courses', 'view', <VersionDetail />)} />
                        <Route path="/curriculum/modules/:id" element={guarded('courses', 'view', <ModuleDetail />)} />
                        {/* The quiz editor. A detail screen reached from
                            ModuleDetail (no nav entry); ?module= carries the
                            authoring read's anchor. */}
                        <Route path="/curriculum/quizzes/:quizId" element={guarded('courses', 'edit', <QuizEditor />)} />
                        <Route path="/programs" element={guarded('courses', 'view', <ProgramsList />)} />
                        <Route path="/programs/:id" element={guarded('courses', 'view', <ProgramDetail />)} />
                        {/* Grade reads the vocabulary; writes inside gate on
                            academy.courses.add / academy.courses.edit (the server's permission). */}
                        <Route path="/check-codes" element={guarded('courses', 'view', <CheckCodes />)} />
                        {/* THE TESTS HR SENDS (STANDALONE-ASSESSMENTS-PLAN §1):
                            named assessments assembled from published screening
                            items. Authoring, so academy.courses — the same door
                            the items themselves are behind; the writes inside
                            carry add / edit / delete. */}
                        <Route path="/assessments" element={guarded('courses', 'view', <Assessments />)} />
                        <Route path="/assessments/:assessmentId" element={guarded('courses', 'view', <AssessmentEditor />)} />
                        {/* academy.pricing.view gates the READ as well as the writes,
                            which is why this is not a courses.view route with
                            guarded controls inside: the promotions half of the
                            screen is the discounts themselves. */}
                        <Route path="/price-book" element={guarded('pricing', 'view', <PriceBook />)} />
                        {/* The public site's editable copy — its own screen,
                            because changing what anonymous visitors read is a
                            publishing decision. See SiteContentEndpoints. */}
                        <Route path="/site-content" element={guarded('website', 'view', <SiteContentEditor />)} />
                        {/* Staff-or-owner on the server: the route itself only
                            requires sign-in, because a learner reads their own. */}
                        <Route path="/enrollments/:id" element={guarded(['enrollments', 'my'], 'view', <EnrollmentDetail />)} />
                        {/* /audit, /audit/:entityName/:entityId and /system/health
                            were here until PLATFORM-PLAN-5 §5a.3. They are Nexus's
                            now — decision 2 (#142) moved ALL of it, entity-scoped
                            trails included, so this app keeps no second door onto
                            a record the console owns. The data did not move: it is
                            published on this app's ops surface (OpsAuditEndpoints)
                            and read from there. */}
                        {/* PLATFORM-PLAN-4 §4j's first wire, read half. academy.cohorts.view
                            and deliberately not a wider read, matching the server:
                            this is for whoever can schedule a session, and a learner
                            has no business knowing which weeks a member of staff is
                            away. */}
                        <Route path="/instructor-availability" element={guarded('cohorts', 'view', <InstructorAvailability />)} />

                        <Route path="/404" element={<NotFound />} />
                        <Route path="*" element={<Navigate to="/404" replace />} />
                    </Routes>
                </Suspense>
            </main>

            {/* Live announcements — a CRM handoff says so on any screen.
                Signed-in only: the hub takes a bearer, and an anonymous
                landing-page visitor has nothing to hear. */}
            {isAuthenticated && <LiveToaster />}

            <AlertModal
                isOpen={alertOpen}
                message={modalMessage}
                type={type}
                duration={type === 'error' ? 0 : 2000}
                onClose={closeAlert}
            />

        </div>
    );
}

export default AppRoutes;
